import * as chatApi from "@lib/chatApi";
import type { ChatQuestion } from "@lib/types";
import { useState } from "react";

interface QuestionCardProps {
  question: ChatQuestion;
}

const OTHER_VALUE = "__other__";

export function QuestionCard({ question }: QuestionCardProps) {
  const [busy, setBusy] = useState(false);
  const [single, setSingle] = useState<string>("");
  const [multi, setMulti] = useState<string[]>([]);
  const [free, setFree] = useState("");
  const [otherText, setOtherText] = useState("");

  const isOpen = question.state === "open";

  async function submit() {
    if (busy) return;
    let answer: string | string[];
    if (question.kind === "single_select") {
      if (!single) return;
      answer = single === OTHER_VALUE ? otherText.trim() : single;
      if (!answer) return;
    } else if (question.kind === "multi_select") {
      if (!multi.length) return;
      answer = multi.map((v) => (v === OTHER_VALUE ? otherText.trim() : v)).filter(Boolean);
      if (!answer.length) return;
    } else {
      if (!free.trim()) return;
      answer = free.trim();
    }

    setBusy(true);
    try {
      await chatApi.answerQuestion(question.id, answer);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await chatApi.cancelQuestion(question.id);
    } finally {
      setBusy(false);
    }
  }

  function toggleMulti(value: string) {
    setMulti((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }

  return (
    <div className="rounded-lg border border-accent/40 bg-surface-primary p-3 text-sm shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        Quick question
      </div>
      <div className="mt-1 text-text-primary">{question.prompt}</div>

      {!isOpen && (
        <p className="mt-3 text-xs italic text-text-tertiary">
          {question.state === "answered" ? "Answered." : "Cancelled."}
        </p>
      )}

      {isOpen && question.kind === "single_select" && (
        <div className="mt-3 space-y-1.5">
          {question.options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-surface-secondary"
            >
              <input
                type="radio"
                name={`q-${question.id}`}
                value={opt.value}
                checked={single === opt.value}
                onChange={() => setSingle(opt.value)}
                className="mt-1"
              />
              <span>
                <span className="text-text-primary">{opt.label}</span>
                {opt.hint && <span className="ml-1 text-xs text-text-tertiary">— {opt.hint}</span>}
              </span>
            </label>
          ))}
          {question.allow_other && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-surface-secondary">
              <input
                type="radio"
                name={`q-${question.id}`}
                value={OTHER_VALUE}
                checked={single === OTHER_VALUE}
                onChange={() => setSingle(OTHER_VALUE)}
                className="mt-1"
              />
              <span className="flex-1">
                <div className="text-text-primary">Other…</div>
                {single === OTHER_VALUE && (
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Type your answer"
                    className="mt-1 w-full rounded-md border border-text-tertiary/30 bg-surface-primary p-1.5 text-xs text-text-primary"
                  />
                )}
              </span>
            </label>
          )}
        </div>
      )}

      {isOpen && question.kind === "multi_select" && (
        <div className="mt-3 space-y-1.5">
          {question.options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-surface-secondary"
            >
              <input
                type="checkbox"
                checked={multi.includes(opt.value)}
                onChange={() => toggleMulti(opt.value)}
                className="mt-1"
              />
              <span>
                <span className="text-text-primary">{opt.label}</span>
                {opt.hint && <span className="ml-1 text-xs text-text-tertiary">— {opt.hint}</span>}
              </span>
            </label>
          ))}
          {question.allow_other && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-surface-secondary">
              <input
                type="checkbox"
                checked={multi.includes(OTHER_VALUE)}
                onChange={() => toggleMulti(OTHER_VALUE)}
                className="mt-1"
              />
              <span className="flex-1">
                <div className="text-text-primary">Other…</div>
                {multi.includes(OTHER_VALUE) && (
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Type your answer"
                    className="mt-1 w-full rounded-md border border-text-tertiary/30 bg-surface-primary p-1.5 text-xs text-text-primary"
                  />
                )}
              </span>
            </label>
          )}
        </div>
      )}

      {isOpen && question.kind === "free_text" && (
        <textarea
          value={free}
          onChange={(e) => setFree(e.target.value)}
          rows={3}
          placeholder="Your answer"
          className="mt-3 w-full rounded-md border border-text-tertiary/30 bg-surface-primary p-2 text-xs text-text-primary"
        />
      )}

      {isOpen && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-60"
          >
            Submit answer
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={cancel}
            className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
